/**
 * Per-printer profile resolution. Matches a discovered printer's name
 * (e.g., "ZEBRA ZD220-203 DPI") against a list of admin-managed
 * profiles by case-insensitive substring on `name_pattern`.
 */

import type { BuildLabelOptions } from './zpl-builder';

export interface PrinterProfile {
  name_pattern: string;
  dpi: number;
  label_width_mm: number;
  label_height_mm: number;
  gap_mm: number;
}

export function matchProfile(
  printerName: string | undefined,
  profiles: PrinterProfile[]
): PrinterProfile | null {
  if (!printerName) return null;
  const upper = printerName.toUpperCase();
  return (
    profiles.find((p) => upper.includes(p.name_pattern.toUpperCase())) ?? null
  );
}

/**
 * Derive an initial DPI guess from a printer's reported name.
 * Falls back to 203 — the default for ZD220 / ZT410 base SKUs.
 */
export function inferDpiFromName(printerName: string | undefined): number {
  if (!printerName) return 203;
  const m = printerName.match(/(\d{3})\s*DPI|-(\d{3})(?:\b|dpi)/i);
  if (m) {
    const dpi = Number(m[1] ?? m[2]);
    if (dpi >= 100 && dpi <= 1200) return dpi;
  }
  if (/ZT610/i.test(printerName)) return 600;
  return 203;
}

export function profileToOptions(p: PrinterProfile): BuildLabelOptions {
  return {
    widthMm: p.label_width_mm,
    heightMm: p.label_height_mm,
    dpi: p.dpi
  };
}
